import { handleError, ValidationError } from '$lib/utils/error-handling';
import { notifications } from '$lib/stores/notifications';
import { filteredQueryResult, scoreFilterRange } from '$lib/stores/selection';
import { filteredMixedResults, mixedResults } from '$lib/stores/mixedResults';
import { get } from 'svelte/store';
import { apiClient } from '$lib/api/client';
import type { CompanyWithContacts } from '$lib/types/api';
import type { CustomerInfo } from '$lib/types';

interface OrderPayload {
  company_name?: string;
  salutation?: string;
  first_name: string;
  last_name: string;
  email: string;
  company_street: string;
  company_house?: string;
  company_zip: string;
  company_city: string;
  company_country: string;
  company_ids: string[];
  contact_ids: string[];
}

/**
 * What `POST /order/order` answers with.
 *
 * Mirrors `createOrderResponse` in internal/httpapi/order.go, which carries
 * these two fields and no others.
 */
interface CreateOrderResponse {
  message?: string;
  order_id?: number;
}

/** What `submitOrder` hands back to the form. */
export interface SubmittedOrder extends CreateOrderResponse {
  /** The order number, or 'Unknown' if the API answered without one. */
  orderId: string;
  status: string;
}

/**
 * What `GET /order/:invoiceId` answers with — what the checkout page polls.
 *
 * Mirrors `orderStatusResponse` in internal/httpapi/order.go, which is the
 * authority; contract.test.ts checks the two agree.
 *
 * One interface used to stand for both responses, and it described neither:
 * it omitted `stripe_payment_url`, which is the field the checkout page
 * redirects on, and offered `total`, `company_count` and `contact_count`,
 * which no version of this API has ever sent.
 */
interface OrderStatusResponse {
  invoice_number?: string;
  payment_status?: string;
  stripe_payment_url?: string;
  company_name?: string;
  email?: string;
  invoice_date?: string | null;
  created_at?: string;
}

export function buildOrderPayload(customer: CustomerInfo): OrderPayload {
  // Check if score filter is active
  const [minScore, maxScore] = get(scoreFilterRange);
  const isScoreFilterActive = minScore !== 0 || maxScore !== 100;

  // Use filtered mixed results if score filter is active, otherwise use all mixed results
  const allMixedResults = get(mixedResults).companies;
  const results: CompanyWithContacts[] = isScoreFilterActive
    ? get(filteredMixedResults)
    : allMixedResults;

  // Validate customer data
  if (!customer.firstName?.trim()) {
    throw new ValidationError('firstName', 'First name is required');
  }

  if (!customer.lastName?.trim()) {
    throw new ValidationError('lastName', 'Last name is required');
  }

  if (!customer.email?.trim()) {
    throw new ValidationError('email', 'Email is required');
  }

  if (!customer.address?.street?.trim()) {
    throw new ValidationError('address.street', 'Street address is required');
  }

  if (!customer.address?.city?.trim()) {
    throw new ValidationError('address.city', 'City is required');
  }

  if (!customer.address?.zipCode?.trim()) {
    throw new ValidationError('address.zipCode', 'ZIP code is required');
  }

  if (!customer.address?.country?.trim()) {
    throw new ValidationError('address.country', 'Country is required');
  }

  // Validate results
  if (!results || results.length === 0) {
    throw new ValidationError('results', 'No companies selected for order');
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(customer.email)) {
    throw new ValidationError('email', 'Invalid email format');
  }

  // Parse salutation from the customer object
  const salutation = customer.salutation || '';

  // Parse house number if provided separately
  const house = customer.address.house || '';

  return {
    company_name: customer.company || undefined,
    salutation: salutation,
    first_name: customer.firstName.trim(),
    last_name: customer.lastName.trim(),
    email: customer.email.trim().toLowerCase(),
    company_street: customer.address.street.trim(),
    company_house: house,
    company_zip: customer.address.zipCode.trim(),
    company_city: customer.address.city.trim(),
    company_country: customer.address.country.trim(),
    company_ids: results.map((r) => r.id).filter(Boolean),
    contact_ids: results.flatMap((r) => (r.contacts || []).map((c) => c.id)).filter(Boolean)
  };
}

export async function submitOrder(customer: CustomerInfo): Promise<SubmittedOrder> {
  try {
    const payload = buildOrderPayload(customer);

    // Note: The order endpoint is at /order/order, not under /deep-iq/
    const response = await apiClient.post<CreateOrderResponse>('/order/order', payload);
    const responseData = response.data;

    // The API returns `order_id`. Two further spellings were tried here —
    // `orderId` and `id` — and neither has ever been sent.
    const orderId = String(responseData.order_id ?? 'Unknown');

    if (response.status === 200) {
      notifications.success(
        `Order ${orderId} created successfully! You will receive a confirmation email shortly.`
      );

      // Return normalized response
      // `status` is this function's own summary: the create response does not
      // carry one, and a 200 is the success.
      return {
        ...responseData,
        orderId,
        status: 'success'
      };
    } else {
      throw new Error('Invalid order response');
    }
  } catch (error) {
    await handleError(error, {
      operation: 'submitOrder',
      component: 'order-api',
      metadata: {
        customerEmail: customer.email,
        companyCount: get(filteredQueryResult).length
      }
    });

    // Don't show notification, let the form handle the error
    throw error;
  }
}

export async function getOrderStatus(orderId: string): Promise<OrderStatusResponse> {
  try {
    if (!orderId?.trim()) {
      throw new ValidationError('orderId', 'Order ID is required');
    }

    const endpoint = `/order/${orderId}`;

    const response = await apiClient.get<OrderStatusResponse>(endpoint);

    return response.data;
  } catch (error) {
    await handleError(error, {
      operation: 'getOrderStatus',
      component: 'order-api',
      metadata: { orderId }
    });

    throw error;
  }
}
